import { Loader2, GitBranch, Package, Play } from "lucide-react";
import React, { useState } from "react";
import { handleError } from "../../lib/errors";
import { createLogger } from "../../lib/logger";
import { Alert, AlertDescription } from "../ui/alert";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const log = createLogger("DemoDeployView");

interface DeploymentStatus {
  deploymentId: string;
  status: string;
  message: string;
  appName: string;
  gitUrl: string;
  timestamp: string;
  deployUrl?: string;
  dockerImage?: string;
  error?: string;
}

export function DemoDeployView() {
  const [repoUrl, setRepoUrl] = useState("");
  const [appName, setAppName] = useState("");
  const [branch, setBranch] = useState("main");
  const [loading, setLoading] = useState(false);
  const [deploymentStatus, setDeploymentStatus] =
    useState<DeploymentStatus | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState("");

  const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8003/api";

  const generateAppName = (url: string) => {
    try {
      const match = url.match(/\/([^/]+?)(?:\.git)?$/);
      return match ? match[1].toLowerCase().replace(/[^a-z0-9-]/g, "-") : "";
    } catch {
      return "";
    }
  };

  const handleRepoUrlChange = (url: string) => {
    setRepoUrl(url);
    if (url && !appName) {
      setAppName(generateAppName(url));
    }
  };

  const deployApp = async () => {
    if (!repoUrl || !appName) {
      setError("Please provide both repository URL and app name");
      return;
    }

    setLoading(true);
    setError("");
    setDeploymentStatus(null);
    setLogs([]);

    try {
      // Start deployment using our new simplified endpoint
      const deployResponse = await fetch(`${API_BASE}/deploy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          gitUrl: repoUrl,
          appName: appName,
          branch: branch,
        }),
      });

      if (!deployResponse.ok) {
        throw new Error(`Deployment failed: ${deployResponse.statusText}`);
      }

      const deployData = await deployResponse.json();

      if (deployData.success) {
        const status: DeploymentStatus = {
          deploymentId: deployData.deploymentId,
          status: deployData.status,
          message: deployData.message,
          appName: deployData.appName,
          gitUrl: deployData.gitUrl,
          timestamp: deployData.timestamp,
        };
        setDeploymentStatus(status);

        // Poll for status updates
        pollDeploymentStatus(deployData.deploymentId);
      }
    } catch (err) {
      // Log centrally; show the friendly message inline (no toast).
      setError(handleError(err, { notify: false, scope: "DemoDeployView" }));
    } finally {
      setLoading(false);
    }
  };

  const pollDeploymentStatus = async (deploymentId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        // Note: We'd need to adjust this endpoint based on actual implementation
        const statusResponse = await fetch(
          `${API_BASE}/deploy/${deploymentId}/status`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();

          // Handle the new API response format
          const deploymentData = statusData.deployment || statusData;
          setDeploymentStatus(deploymentData);

          // Stop polling if deployment is complete
          if (["success", "failed", "error"].includes(deploymentData.status)) {
            clearInterval(pollInterval);

            // Fetch logs
            fetchLogs(deploymentId);
          }
        }
      } catch (err) {
        log.error("Failed to poll status", err);
      }
    }, 2000);

    // Stop polling after 5 minutes
    setTimeout(() => clearInterval(pollInterval), 300000);
  };

  const fetchLogs = async (deploymentId: string) => {
    try {
      const logsResponse = await fetch(
        `${API_BASE}/deploy/${deploymentId}/logs`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (logsResponse.ok) {
        const logsData = await logsResponse.json();
        setLogs(logsData.logs || []);
      }
    } catch (err) {
      log.error("Failed to fetch logs", err);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
        return "text-success";
      case "failed":
      case "error":
        return "text-destructive";
      case "pending":
      case "cloning":
      case "building":
      case "deploying":
        return "text-primary";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="mx-auto max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-display mb-2">
            🚀 Demo Deployment
          </h1>
          <p className="text-muted-foreground text-lg">
            Deploy any GitHub repository instantly to your local environment
          </p>
        </div>

        {/* Deployment Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Deploy Your App
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="repoUrl">Repository URL</Label>
              <Input
                id="repoUrl"
                placeholder="https://github.com/username/repository.git"
                value={repoUrl}
                onChange={(e) => handleRepoUrlChange(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="appName">App Name</Label>
                <Input
                  id="appName"
                  placeholder="my-awesome-app"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="branch">Branch</Label>
                <Input
                  id="branch"
                  placeholder="main"
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={deployApp}
              disabled={loading || !repoUrl || !appName}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deploying...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Deploy Now
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Deployment Status */}
        {deploymentStatus && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GitBranch className="h-5 w-5" />
                Deployment Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Status:</span>
                <span
                  className={`font-semibold ${getStatusColor(
                    deploymentStatus.status
                  )}`}
                >
                  {deploymentStatus.status.charAt(0).toUpperCase() +
                    deploymentStatus.status.slice(1)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-medium">App Name:</span>
                <span>{deploymentStatus.appName}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="font-medium">Repository:</span>
                <span className="text-sm text-muted-foreground truncate max-w-xs">
                  {deploymentStatus.gitUrl}
                </span>
              </div>

              {deploymentStatus.deployUrl && (
                <div className="flex items-center justify-between">
                  <span className="font-medium">Access URL:</span>
                  <a
                    href={deploymentStatus.deployUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {deploymentStatus.deployUrl}
                  </a>
                </div>
              )}

              {deploymentStatus.dockerImage && (
                <div className="flex items-center justify-between">
                  <span className="font-medium">Docker Image:</span>
                  <span className="text-sm text-muted-foreground">
                    {deploymentStatus.dockerImage}
                  </span>
                </div>
              )}

              <div className="text-sm text-muted-foreground">
                <strong>Message:</strong> {deploymentStatus.message}
              </div>

              {deploymentStatus.error && (
                <Alert variant="destructive">
                  <AlertDescription>{deploymentStatus.error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Deployment Logs */}
        {logs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Deployment Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-terminal text-terminal-foreground p-4 rounded-md font-mono text-sm max-h-96 overflow-y-auto">
                {logs.map((log, index) => (
                  <div key={index} className="mb-1">
                    {log}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info Section */}
        <Card className="bg-primary/10">
          <CardContent className="pt-6">
            <h3 className="mb-2 text-h5">How it works:</h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
              <li>Enter your GitHub repository URL</li>
              <li>We'll clone your repository locally</li>
              <li>Build a Docker image from your code</li>
              <li>Deploy it to your local Kubernetes cluster</li>
              <li>Access your app via the provided URL</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
